/**
 * API Client for SlideFactory backend
 *
 * Handles authentication, token refresh, and provides
 * type-safe API methods.
 */

import { runSharedRefresh, type RefreshState } from './sharedRefresh'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const REFRESH_TIMEOUT_MS = 15_000

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  params?: Record<string, string | number | boolean>
  headers?: Record<string, string>
  /**
   * Read the body as bytes. A PNG through `text()` is no longer a valid file, and that is the
   * whole reason this way exists (EXP-1542, FR-006).
   */
  responseType?: 'blob'
}

interface ApiResponse<T = unknown> {
  data: T
  status: number
  headers: Headers
}

class ApiClient {
  private accessToken: string | null = null
  private onSessionExpiredCallback: (() => void) | null = null
  private onAccessTokenChangedCallback: ((token: string | null) => void) | null = null
  private refreshState: RefreshState = { pending: null }
  private sessionExpiredNotified = false
  private tokenGeneration = 0

  constructor() {
    // Load token from localStorage on init
    this.accessToken = localStorage.getItem('accessToken')
  }

  setAccessToken(token: string | null): void {
    this.tokenGeneration++
    this.refreshState.pending = null
    this.updateAccessToken(token)
  }

  private updateAccessToken(token: string | null): void {
    this.accessToken = token
    if (token) this.sessionExpiredNotified = false
    this.onAccessTokenChangedCallback?.(token)
  }

  onAccessTokenChanged(callback: (token: string | null) => void): void {
    this.onAccessTokenChangedCallback = callback
  }

  onSessionExpired(callback: () => void): void {
    this.onSessionExpiredCallback = callback
  }

  private buildUrl(path: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(path, window.location.origin)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    // `responseType` comes out of the options here and does not travel on in
    // `restOptions`: `fetch` would get an unknown key, ignore it without a word, and the
    // saved file would carry text instead of bytes (FR-006).
    const { params, body, headers: customHeaders, responseType, ...restOptions } = options

    const url = this.buildUrl(`${API_BASE_URL}${path}`, params)

    // A multipart body carries its own boundary in the Content-Type the browser writes.
    // Setting the header by hand would drop the boundary and the server would see no fields.
    const isForm = body instanceof FormData

    const headers: Record<string, string> = {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...customHeaders
    }

    // Add authorization header if we have a token
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : (isForm ? body : JSON.stringify(body)),
      credentials: 'include', // Include cookies for refresh token
      ...restOptions
    })

    // Handle 401 - try token refresh
    if (response.status === 401 && path !== '/api/auth/refresh' && path !== '/api/auth/login') {
      const refreshed = await this.refreshToken()
      if (refreshed) {
        // Retry the original request
        headers['Authorization'] = `Bearer ${this.accessToken}`
        // The same FormData goes out a second time — the retry after a token renewal is the
        // whole point of routing file transfers through this client (FR-062).
        const retryResponse = await fetch(url, {
          method,
          headers,
          body: body === undefined ? undefined : (isForm ? body : JSON.stringify(body)),
          credentials: 'include',
          ...restOptions
        })
        return this.handleResponse<T>(retryResponse, responseType)
      }
    }

    return this.handleResponse<T>(response, responseType)
  }

  private async handleResponse<T>(
    response: Response,
    responseType?: 'blob'
  ): Promise<ApiResponse<T>> {
    let data: T

    const contentType = response.headers.get('content-type')
    // Only the successful answer branches off. A refused one carries `application/json`, runs
    // through the branch below and becomes an ApiError with its `detail` — the sentence beside
    // the pictures lives on that (FR-021, FR-023).
    if (response.ok && responseType === 'blob') {
      data = await response.blob() as unknown as T
    } else if (contentType?.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text() as unknown as T
    }

    if (!response.ok) {
      const error = new ApiError(
        (data as any)?.detail || (data as any)?.message || 'Request failed',
        response.status,
        data
      )
      throw error
    }

    return {
      data,
      status: response.status,
      headers: response.headers
    }
  }

  waitForPendingRefresh(): Promise<boolean> {
    return this.refreshState.pending ?? Promise.resolve(false)
  }

  refreshToken(): Promise<boolean> {
    return runSharedRefresh(this.refreshState, () => this.performTokenRefresh())
  }

  private async performTokenRefresh(): Promise<boolean> {
    const generation = this.tokenGeneration
    const controller = new AbortController()
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort()
        reject(new DOMException('Token refresh timed out', 'AbortError'))
      }, REFRESH_TIMEOUT_MS)
    })
    try {
      // Race both stages so even a transport that ignores abort cannot hold the renewal.
      const response = await Promise.race([fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal
      }), timeout])

      if (generation !== this.tokenGeneration) return false

      if (response.ok) {
        const data = await Promise.race([response.json(), timeout])
        if (generation !== this.tokenGeneration) return false
        localStorage.setItem('accessToken', data.access_token)
        this.updateAccessToken(data.access_token)
        return true
      }
      if (response.status !== 401 && response.status !== 403) return false
    } catch {
      // Transport failures leave the session available for another attempt.
      return false
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId)
    }

    // Clear invalid token
    localStorage.removeItem('accessToken')
    this.updateAccessToken(null)

    // Notify once for this expired session, even if another request fails later.
    if (!this.sessionExpiredNotified) {
      this.sessionExpiredNotified = true
      this.onSessionExpiredCallback?.()
    }

    return false
  }

  /**
   * Ensure the access token is valid by calling /api/auth/validate.
   * If the token is expired, the 401 interceptor will refresh it automatically.
   * Returns true if a valid token is available, false if validation fails.
   */
  async ensureValidToken(): Promise<boolean> {
    try {
      await this.request('GET', '/api/auth/validate')
      return true
    } catch {
      return false
    }
  }

  // HTTP methods
  async get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, options)
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { ...options, body: body as any })
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { ...options, body: body as any })
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { ...options, body: body as any })
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, options)
  }
}

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// Export singleton instance
export const api = new ApiClient()

// Export types
export type { ApiResponse, RequestOptions }
